import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import PropTypes from "prop-types";

import {
  login as apiLogin,
  refreshToken as refreshTokenApi,
} from "../api/auth-api";
import { log } from "../logger.js";
import jwt_decode from "jwt-decode";

export const ACCESS_TOKEN_KEY = "accessToken";
export const REFRESH_TOKEN_KEY = "refreshToken";

const getDefaultValue = () => ({
  isAuthenticated: false,
  isInitialized: false,
});

const handlers = {
  LOGIN: (state, action) => {
    const { isAuthenticated } = action.payload;
    return {
      ...state,
      isAuthenticated: isAuthenticated,
      isInitialized: true,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isInitialized: true,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...getDefaultValue(),
  login: () => Promise.resolve(),
  loginByToken: () => {
  },
  logout: () => {
  },
  refreshToken: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, getDefaultValue());
  const refreshTokenPromise = useRef();

  useEffect(() => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const isAuthenticated = !!(accessToken && refreshToken);
    if (isAuthenticated)
      dispatch({
        type: "LOGIN",
        payload: {
          isAuthenticated,
        },
      });
    else logout();
  }, []);

  const handleTokenRes = useCallback((res) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, res.data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, res.data.refreshToken);

    dispatch({
      type: "LOGIN",
      payload: {
        isAuthenticated: true,
      },
    });
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    return handleTokenRes(res);
  };

  const loginByToken = async (res) => {
    handleTokenRes(res);
  };

  const refreshToken = async () => {
    if (!refreshTokenPromise.value) {
      refreshTokenPromise.value = new Promise((resolve, reject) => {
        const existingToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        log.debug("refreshTokenApi");
        refreshTokenApi(existingToken)
          .then((res) => {
            handleTokenRes(res);
            resolve(res);
          })
          .catch(reject)
          .finally(() => {
            refreshTokenPromise.value = null;
          });
      });
    } else {
      log.debug("Already refreshing token. Wait.");
      await refreshTokenPromise.value;
      log.debug("Resume call");
    }
  };

  const logout = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    dispatch({ type: "LOGOUT" });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        loginByToken,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
